import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { tastingsApi } from '../../lib/api';
import { DataTable, Pagination } from '../../components/ui/DataTable';
import { MaturityStateBadge } from '../../components/ui/StatusBadge';
import { formatDate } from '../../lib/utils';
import { useAuth } from '../../hooks/useAuth';
const columns = [
    {
        key: 'wine',
        header: 'Wine',
        render: (row) => (_jsxs("div", { children: [_jsx("p", { className: "font-medium text-gray-900", children: row.wineName }), _jsxs("p", { className: "text-xs text-gray-500", children: [row.producerName, row.vintage ? ` · ${row.vintage}` : ''] })] })),
    },
    {
        key: 'date',
        header: 'Date',
        render: (row) => formatDate(row.openedDate),
        className: 'w-28 text-gray-600',
    },
    {
        key: 'score',
        header: 'Score',
        render: (row) => {
            if (!row.ratingScore)
                return _jsx("span", { className: "text-gray-300", children: "\u2014" });
            const max = row.ratingScale === 'parker_100' ? '100'
                : row.ratingScale === 'jancis_20' ? '20'
                    : row.ratingScale === 'custom_5' ? '5'
                        : '10';
            return (_jsxs("span", { className: "font-semibold text-wine-700", children: [row.ratingScore, "/", max] }));
        },
        className: 'w-20',
    },
    {
        key: 'assessment',
        header: 'Assessment',
        render: (row) => row.drinkWindowAssessment
            ? _jsx(MaturityStateBadge, { status: row.drinkWindowAssessment })
            : _jsx("span", { className: "text-gray-300", children: "\u2014" }),
    },
    {
        key: 'note',
        header: 'Note',
        render: (row) => (_jsx("p", { className: "text-sm text-gray-600 truncate max-w-xs", children: row.overallTastingNote ?? (row.noseNotes ?? '—') })),
    },
    {
        key: 'flawed',
        header: '',
        render: (row) => row.wasFlawed
            ? _jsx("span", { className: "badge bg-red-100 text-red-700", children: "Flawed" })
            : null,
        className: 'w-20',
    },
];
export function TastingLedgerPage() {
    const navigate = useNavigate();
    const { activeAccount } = useAuth();
    const [page, setPage] = useState(1);
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const { data, isLoading } = useQuery({
        queryKey: ['tastings', activeAccount?.id, page, from, to],
        queryFn: () => tastingsApi.list({
            page,
            from: from || undefined,
            to: to || undefined,
        }),
        enabled: Boolean(activeAccount),
    });
    return (_jsxs("div", { className: "space-y-4", children: [_jsx("div", { className: "flex items-center justify-between", children: _jsxs("div", { children: [_jsx("h1", { className: "page-title", children: "Tasting Ledger" }), _jsxs("p", { className: "text-sm text-gray-500 mt-0.5", children: [data?.total ?? 0, " tasting notes"] })] }) }), _jsxs("div", { className: "flex items-center gap-3", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("label", { className: "text-sm text-gray-500", children: "From" }), _jsx("input", { type: "date", value: from, onChange: (e) => { setFrom(e.target.value); setPage(1); }, className: "input w-40" })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("label", { className: "text-sm text-gray-500", children: "To" }), _jsx("input", { type: "date", value: to, onChange: (e) => { setTo(e.target.value); setPage(1); }, className: "input w-40" })] }), (from || to) && (_jsx("button", { onClick: () => { setFrom(''); setTo(''); setPage(1); }, className: "text-sm text-wine-700 hover:text-wine-800", children: "Clear" }))] }), _jsxs("div", { className: "card overflow-hidden", children: [_jsx(DataTable, { columns: columns, data: data?.tastings ?? [], isLoading: isLoading, rowKey: (row) => row.id, onRowClick: (row) => navigate(`/bottles/${row.bottleId}`), emptyTitle: "No tasting notes", emptyDescription: "Tasting notes added to bottles will appear here" }), _jsx(Pagination, { page: page, pageSize: 50, total: data?.total ?? 0, onPageChange: setPage })] })] }));
}
